import type { JSX, PropsWithChildren } from 'react';

import { TextField } from '@change/ds-forms';

import { useI18n } from '@change-corgi/core/react/i18n';

import type { Field, SmartFieldEvent } from 'src/app/shared/utils/payments';

import { StyleWrapper } from '../../components/StyleWrapper';

import { DlocalButton } from './DlocalButton';
import { DLocalSmartField } from './DLocalSmartField';
import type { DLocalValidationError } from './types';

type Props = {
	visible: boolean;
	handleSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
	cardHolder: string | undefined;
	onChangeCardHolderName: (value: string) => void;
	error: DLocalValidationError;
	disabled: boolean;
	loading: boolean;
	onCardHolderNameFocus: () => void;
	useDonationLanguage: boolean;
	onCardExpiryFieldFocus: () => void;
	onCardCVCFieldFocus: () => void;
	updateError: (field: string, event: SmartFieldEvent) => void;
	onCardNumberFieldFocus: () => void;
	setPan: (field: Field | null) => void;
};

export function DLocalCreditCardForm({
	visible,
	handleSubmit,
	cardHolder,
	children,
	onChangeCardHolderName,
	error,
	disabled,
	loading,
	onCardHolderNameFocus,
	useDonationLanguage,
	onCardExpiryFieldFocus,
	onCardCVCFieldFocus,
	updateError,
	onCardNumberFieldFocus,
	setPan,
}: PropsWithChildren<Props>): JSX.Element {
	const { translate } = useI18n();

	return (
		<form
			className={visible ? 'block' : 'hidden'}
			data-testid="dlocal-credit-card-form"
			onSubmit={handleSubmit}
			data-qa="dlocal-credit-card-form"
		>
			<div className="flex flex-col gap-4">
				<TextField
					data-testid="card-holder"
					data-qa="dlocal-credit-card-form-cardholder-name-input"
					label={translate('fe.components.credit_card.cardholder_name')}
					name="card-holder-name"
					value={cardHolder}
					onChange={onChangeCardHolderName}
					onFocus={onCardHolderNameFocus}
				/>
				<div className="relative">
					<div data-testid="credit-card-number" data-qa="dlocal-credit-card-form-card-number">
						<StyleWrapper
							label={translate('corgi.payments.stripe_elements.card_number')}
							errorMessage={error.card}
							onFocus={onCardNumberFieldFocus}
							onBlur={(event) => updateError('card', event as SmartFieldEvent)}
						>
							<DLocalSmartField type="pan" onFieldCreated={setPan} placeholder="4111 1111 1111 1111" />
						</StyleWrapper>
					</div>
					<div className="absolute right-0 top-0">
						<img
							alt={translate('corgi.payments.stripe_elements.card_image_alt_text')}
							src="https://assets.change.org/app-assets/victory_fund/credit-card-icons.png"
							height="20px"
							width="127px"
						/>
					</div>
				</div>
				<div className="flex gap-2">
					<div className="flex-1" data-testid="credit-card-expiry" data-qa="dlocal-credit-card-form-card-expiry">
						<StyleWrapper
							label={translate('corgi.payments.stripe_elements.expiration')}
							errorMessage={error.expire}
							onFocus={onCardExpiryFieldFocus}
							onBlur={(event) => updateError('expire', event as SmartFieldEvent)}
						>
							<DLocalSmartField type="expiration" />
						</StyleWrapper>
					</div>

					<div className="flex-1" data-testid="credit-card-cvc" data-qa="dlocal-credit-card-form-card-cvc">
						<StyleWrapper
							label={translate('corgi.payments.stripe_elements.cvc')}
							errorMessage={error.cvv}
							onFocus={onCardCVCFieldFocus}
							onBlur={(event) => updateError('cvv', event as SmartFieldEvent)}
						>
							<DLocalSmartField type="cvv" />
						</StyleWrapper>
					</div>
				</div>
				{children}
				<div>
					<DlocalButton disabled={disabled || loading} loading={loading} useDonationLanguage={useDonationLanguage} />
				</div>
			</div>
		</form>
	);
}
